"use client";

import { useState } from "react";
import { css, cx } from "styled-system/css";
import { cf } from "@/components/brand/sfturnos-styles";
import { useToast } from "@/hooks/useToast";

const days = [
  { v: 0, label: "Domingo" },
  { v: 1, label: "Lunes" },
  { v: 2, label: "Martes" },
  { v: 3, label: "Miércoles" },
  { v: 4, label: "Jueves" },
  { v: 5, label: "Viernes" },
  { v: 6, label: "Sábado" },
];

type ScheduleRow = {
  id: string;
  dayOfWeek: number;
  startTime: string;
  endTime: string;
  slotDuration: number;
  interval: number;
};

export function ScheduleManager({ schedules: initialSchedules }: { schedules: ScheduleRow[] }) {
  const toast = useToast();
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [pendingScheduleId, setPendingScheduleId] = useState<string | null>(null);

  async function delSchedule(id: string) {
    setBusy(true);
    const r = await fetch(`/api/dashboard/schedules/${id}`, { method: "DELETE" });
    setBusy(false);
    setPendingScheduleId(null);
    if (!r.ok) {
      toast.error("No se pudo quitar el horario.");
      return;
    }
    toast.success("Horario quitado");
    window.location.reload();
  }

  async function addSchedule(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setErr(null);
    const fd = new FormData(e.currentTarget);
    const body = {
      dayOfWeek: Number(fd.get("dayOfWeek")),
      startTime: String(fd.get("startTime")),
      endTime: String(fd.get("endTime")),
      slotDuration: Number(fd.get("slotDuration")),
      interval: Number(fd.get("interval") ?? 0) || 0,
    };
    setBusy(true);
    const r = await fetch("/api/dashboard/schedules", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    });
    setBusy(false);
    if (!r.ok) {
      const j = (await r.json()) as { error?: string };
      setErr(j.error ?? "No se pudo guardar");
      return;
    }
    (e.target as HTMLFormElement).reset();
    toast.success("Franja agregada");
    window.location.reload();
  }

  const input = cx(cf.input, css({ h: "10", fontSize: "sm" }));
  const labelSm = css({ fontSize: "xs", color: "#64748b" });

  return (
    <>
      {err ? <p className={css({ fontSize: "sm", color: "#b91c1c", mb: "4" })}>{err}</p> : null}

      <section className={css({ mb: "10" })}>
        <h2 className={css({ fontSize: "sm", fontWeight: "semibold", color: "#0f172a", mb: "4" })}>
          Horario semanal
        </h2>
        <p className={css({ fontSize: "sm", color: "#64748b", mb: "4", maxW: "40rem", lineHeight: "1.5" })}>
          Definí en qué días y franjas aceptás reservas online. Podés sumar varios bloques el mismo día (ej. mañana y tarde).
        </p>
        <p className={css({ fontSize: "sm", color: "#64748b", mb: "4", maxW: "42rem", lineHeight: "1.5" })}>
          Para bloquear días o rangos de fechas usá{" "}
          <a href="/dashboard/schedule/blocked-dates" className={css({ color: "token(colors.brand.600)", fontWeight: "semibold" })}>
            Inhabilitar fechas
          </a>
          .
        </p>

        <form
          onSubmit={(e) => void addSchedule(e)}
          className={cx(cf.dashSurface, css({ p: "5", mb: "6", display: "grid", gap: "4" }))}
        >
          <p className={css({ fontWeight: "semibold", color: "#0f172a", fontSize: "sm" })}>Agregar franja</p>
          <div
            className={css({
              display: "grid",
              gridTemplateColumns: { base: "1fr", sm: "repeat(2, 1fr)", md: "repeat(3, 1fr)", lg: "repeat(5, 1fr)" },
              gap: "4",
            })}
          >
            <label className={css({ display: "flex", flexDir: "column", gap: "1" })}>
              <span className={labelSm}>Día</span>
              <select name="dayOfWeek" required className={input} disabled={busy}>
                {days.map((d) => (
                  <option key={d.v} value={d.v}>
                    {d.label}
                  </option>
                ))}
              </select>
            </label>
            <label className={css({ display: "flex", flexDir: "column", gap: "1" })}>
              <span className={labelSm}>Desde</span>
              <input name="startTime" type="time" required className={input} disabled={busy} defaultValue="09:00" />
            </label>
            <label className={css({ display: "flex", flexDir: "column", gap: "1" })}>
              <span className={labelSm}>Hasta</span>
              <input name="endTime" type="time" required className={input} disabled={busy} defaultValue="18:00" />
            </label>
            <label className={css({ display: "flex", flexDir: "column", gap: "1" })}>
              <span className={labelSm}>Duración turno</span>
              <select name="slotDuration" className={input} disabled={busy} defaultValue={30}>
                <option value={15}>15 min</option>
                <option value={30}>30 min</option>
                <option value={45}>45 min</option>
                <option value={60}>60 min</option>
              </select>
            </label>
            <label className={css({ display: "flex", flexDir: "column", gap: "1" })}>
              <span className={labelSm}>Pausa entre turnos (min)</span>
              <input name="interval" type="number" min={0} max={120} className={input} disabled={busy} defaultValue={0} />
            </label>
          </div>
          <button
            type="submit"
            disabled={busy}
            className={css({
              justifySelf: "start",
              px: "4",
              py: "2.5",
              rounded: "lg",
              fontSize: "sm",
              fontWeight: "semibold",
              color: "white",
              bg: "linear-gradient(90deg, token(colors.brand.600), token(colors.brand.500))",
              border: "none",
              cursor: busy ? "wait" : "pointer",
              opacity: busy ? 0.7 : 1,
            })}
          >
            Agregar horario
          </button>
        </form>

        {initialSchedules.length === 0 ? (
          <p className={css({ fontSize: "sm", color: "#64748b" })}>Todavía no cargaste franjas.</p>
        ) : (
          <ul className={css({ display: "flex", flexDir: "column", gap: "3" })}>
            {initialSchedules.map((s) => (
              <li
                key={s.id}
                className={cx(
                  cf.dashSurface,
                  css({
                    p: "4",
                    display: "flex",
                    flexWrap: "wrap",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: "3",
                  }),
                )}
              >
                <span className={css({ fontSize: "sm", color: "#334155" })}>
                  <strong className={css({ color: "#0f172a" })}>{days.find((d) => d.v === s.dayOfWeek)?.label}</strong>
                  <span className={css({ mx: "2", opacity: 0.4 })}>·</span>
                  {s.startTime} – {s.endTime}
                  <span className={css({ color: "#64748b", ml: "2" })}>
                    (turno {s.slotDuration} min
                    {s.interval > 0 ? ` + ${s.interval} min pausa` : ""})
                  </span>
                </span>
                {pendingScheduleId === s.id ? (
                  <span className={css({ display: "flex", gap: "2", flexWrap: "wrap" })}>
                    <span className={css({ fontSize: "xs", color: "#64748b", alignSelf: "center" })}>¿Quitar esta franja?</span>
                    <button
                      type="button"
                      disabled={busy}
                      onClick={() => void delSchedule(s.id)}
                      className={css({
                        fontSize: "xs",
                        color: "white",
                        bg: "#b91c1c",
                        border: "none",
                        px: "3",
                        py: "1.5",
                        rounded: "md",
                        cursor: "pointer",
                      })}
                    >
                      Sí
                    </button>
                    <button
                      type="button"
                      disabled={busy}
                      onClick={() => setPendingScheduleId(null)}
                      className={css({
                        fontSize: "xs",
                        color: "#334155",
                        bg: "#f1f5f9",
                        borderWidth: "1px",
                        borderColor: "#e2e8f0",
                        px: "3",
                        py: "1.5",
                        rounded: "md",
                        cursor: "pointer",
                      })}
                    >
                      No
                    </button>
                  </span>
                ) : (
                  <button
                    type="button"
                    disabled={busy}
                    onClick={() => setPendingScheduleId(s.id)}
                    className={css({
                      fontSize: "xs",
                      color: "#fecaca",
                      bg: "transparent",
                      borderWidth: "1px",
                      borderColor: "rgba(248,113,113,0.4)",
                      px: "3",
                      py: "1.5",
                      rounded: "md",
                      cursor: "pointer",
                      _hover: { bg: "rgba(248,113,113,0.1)" },
                    })}
                  >
                    Quitar
                  </button>
                )}
              </li>
            ))}
          </ul>
        )}
      </section>
    </>
  );
}
